import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { ComponentCloseService } from '../../../services/component-close.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'lib-object-dropdown',
  templateUrl: './object-dropdown.component.html',
  styleUrls: ['./object-dropdown.component.scss'],
  standalone: false
})
export class ObjectDropdownComponent<T = any> implements OnChanges, OnInit {
  @Input() items: T[] = [];                    
  @Input() displayField!: keyof T;      
  @Input() initialValue: T | T[] | null = null;
  @Input() disabled: boolean = false;
  @Input() isRequired: boolean = false;    
  @Input() placeholder: string | null = null;
  @Input() isSearchEnabled: boolean = false;
  @Input() maxDisplayItemSize!: number | null;
  @Input() trackByField!: keyof T;
  @Input() isMultiSelect: boolean = false;
  @Input() customClass?: string;
  @Output() valueChange = new EventEmitter<T | null>();
  @Output() multiValueChange = new EventEmitter<T[]>();
  @Output() searchTextChange = new EventEmitter<string>();
  isOpen = false;
  selectedItem: T | null = null;
  selectedItems: T[] = [];
  searchValue: string = '';

  private searchSubject = new Subject<string>();

  constructor(private closeService: ComponentCloseService) {
   this.searchSubject
    .pipe(
      debounceTime(500),
      distinctUntilChanged()
    )
    .subscribe(searchText => {
      this.searchTextChange.emit(searchText);
    });
  }

  ngOnInit() {
    this.closeService.closeAll$.subscribe(type => {
      if (type === 'dropdown') {
        this.isOpen = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['initialValue']) {

      if (!this.initialValue) {
        this.selectedItem = null;
        this.selectedItems = [];
        return;
      }

      if (this.isMultiSelect) {
        const incoming = Array.isArray(this.initialValue)
          ? this.initialValue
          : [this.initialValue];

        // Only update if actually different
        if (!this.areArraysEqualByTrackField(this.selectedItems, incoming)) {
          this.selectedItems = [...incoming];
        }

      } else {
        const incoming = Array.isArray(this.initialValue)
          ? this.initialValue[0] ?? null
          : this.initialValue;

        if (
          !this.selectedItem ||
          this.selectedItem[this.trackByField] !== incoming?.[this.trackByField]
        ) {
          this.selectedItem = incoming;
        }
      }
    }

    if (changes['items'] && this.items?.length) {

      if (this.isMultiSelect && this.selectedItems.length) {

        const filtered = this.selectedItems.filter(selected =>
          this.items.some(item =>
            item[this.trackByField] === selected[this.trackByField]
          )
        );

        // Only assign if something was removed
        if (!this.areArraysEqualByTrackField(this.selectedItems, filtered)) {
          this.selectedItems = filtered;
        }

      } else if (this.selectedItem) {

        const stillExists = this.items.some(item =>
          item[this.trackByField] === this.selectedItem![this.trackByField]
        );

        if (!stillExists) {
          this.selectedItem = null;
        }
      }
    }
  }

  private areArraysEqualByTrackField(a: any[] = [], b: any[] = []): boolean {
    if (a.length !== b.length) return false;

    return a.every(aItem =>
      b.some(bItem => bItem[this.trackByField] === aItem[this.trackByField])
    );
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    
    if (this.isOpen) {
      this.isOpen = false;
      return;
    }

    if (!this.disabled) {
      this.closeService.close('dropdown');
      this.isOpen = !this.isOpen;
      
      if (this.isOpen && this.isSearchEnabled) {
        this.searchValue = '';
        this.searchTextChange.emit('');
      }
    }
  }

  selectItem(item: T | null, event?: Event) {
    event?.stopPropagation();
    
    if (this.isMultiSelect) {
      if (item === null) {
        this.selectedItems = [];
        this.multiValueChange.emit([]); // Emituj prazan niz odmah
        return; // Prekidamo izvršavanje ovde
      }

      const exists = this.selectedItems.some(
        selected => selected[this.trackByField] === item?.[this.trackByField]
      );
      
      if (exists) {
        this.selectedItems = this.selectedItems.filter(
          selected => selected[this.trackByField] !== item?.[this.trackByField]
        );
      } else {
        this.selectedItems = [...this.selectedItems, item];
      }

      this.multiValueChange.emit([...this.selectedItems]);
    } else {
      // Single select logika
      this.selectedItem = item;
      this.searchValue = '';
      this.valueChange.emit(item);
      this.isOpen = false;
    }
  }

  isItemSelected(item: T): boolean {
    if (!this.isMultiSelect || !item) return false;
    return this.selectedItems.some(
      selected => selected[this.trackByField] === item[this.trackByField]
    );
  }

  getDisplayText(): string {
    if (this.isMultiSelect) {
      if (this.selectedItems.length === 0) {
        return this.placeholder || 'Select...';
      }
      if (this.selectedItems.length === 1) {
        return String(this.selectedItems[0][this.displayField]);
      }
      return this.selectedItems
        .map(item => String(item[this.displayField]))
        .join(', ');
    } else {
      return this.selectedItem ? String(this.selectedItem[this.displayField]) : (this.placeholder || 'Select...');
    }
  }

  onSearchInput(event: Event) {
    event?.stopPropagation();
    const input = event.target as HTMLInputElement;
    this.searchValue = input.value;
    
    this.searchSubject.next(this.searchValue);
  }

  @HostListener('document:click')
  closeActionMenu() {
    this.isOpen = false;
    this.searchValue = '';
  }
}
